import { AlertTriangle, Check, Loader2, RefreshCw, ShieldCheck } from "lucide-react";

import { useAronRetryWithCritique } from "../api/aronApi";
import type { AronQualityGate as Gate } from "../types";

/** Niezależny recenzent odpowiedzi ARON-a.
 *
 * Domyślnie NIC nie cofa — pokazuje ocenę i to człowiek decyduje, czy prosić
 * o poprawkę. Wcześniej ocena doklejała się do treści jako zwykły akapit, przez co
 * ginęła w odpowiedzi; tutaj jest osobnym blokiem z własną hierarchią. */
export function AronQualityGate({
  gate,
  conversationId,
}: {
  gate: Gate;
  conversationId: string | null;
}) {
  const poprawka = useAronRetryWithCritique();

  if (gate.phase === "start" && gate.score == null) {
    return (
      <div className="mt-2 flex items-center gap-2 border border-dashed border-gray-300 px-2.5 py-1.5 text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400">
        <Loader2 size={12} className="animate-spin" />
        <span>{gate.status || "Kontroler jakości sprawdza odpowiedź…"}</span>
      </div>
    );
  }
  if (gate.score == null) return null;

  const ok = gate.passed !== false && (gate.threshold == null || gate.score >= gate.threshold);

  const popros = () => {
    if (!conversationId) return;
    poprawka.mutate({ conversationId, critique: gate.reason || "" });
  };

  return (
    <div
      className={`mt-2 border-l-[3px] text-xs ${
        ok
          ? "border-l-emerald-600 bg-emerald-50/60 dark:bg-emerald-950/20"
          : "border-l-amber-500 bg-amber-50/60 dark:bg-amber-950/20"
      }`}
    >
      <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
        <div className="flex items-center gap-1.5 font-medium text-gray-800 dark:text-gray-100">
          {ok ? (
            <ShieldCheck size={13} className="text-emerald-700 dark:text-emerald-400" />
          ) : (
            <AlertTriangle size={13} className="text-amber-600 dark:text-amber-400" />
          )}
          <span>Recenzent</span>
          <span className="tabular-nums text-gray-500 dark:text-gray-400">
            {gate.score}/100
          </span>
          {gate.threshold != null && (
            <span className="text-gray-400">· próg {gate.threshold}</span>
          )}
          {gate.attempt != null && gate.attempt > 1 && (
            <span className="text-gray-400">· podejście {gate.attempt}</span>
          )}
        </div>
        {!ok &&
          (poprawka.isSuccess ? (
            <span className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
              <Check size={12} /> poprawka w toku
            </span>
          ) : (
            <button
              type="button"
              onClick={popros}
              disabled={poprawka.isPending || !conversationId}
              title={conversationId ? undefined : "Otwórz rozmowę, żeby poprosić o poprawkę"}
              className="flex items-center gap-1 border border-gray-300 bg-white px-2 py-0.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              {poprawka.isPending ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <RefreshCw size={11} />
              )}
              Poproś o poprawkę
            </button>
          ))}
      </div>
      {poprawka.isError && (
        <p className="border-t border-red-200 px-2.5 py-1.5 text-red-700 dark:border-red-900/50 dark:text-red-300">
          Nie udało się zamówić poprawki. Spróbuj ponownie — poprzednia odpowiedź
          zostaje na miejscu.
        </p>
      )}
      {!ok && gate.reason && (
        <p className="border-t border-amber-200/70 px-2.5 py-1.5 leading-relaxed text-gray-600 dark:border-amber-900/40 dark:text-gray-300">
          {gate.reason}
        </p>
      )}
    </div>
  );
}
